import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, User, Chrome } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/auth";
import Logo from "@/components/Logo";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const intent = searchParams.get("intent");
  const [mode, setMode] = useState("signup");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const postAuth = async () => {
    if (intent === "checkout") {
      try {
        const res = await api.post("/payments/checkout", { origin_url: window.location.origin });
        window.location.href = res.data.checkout_url;
        return;
      } catch {
        toast.error("Could not start checkout — head to your dashboard to subscribe.");
      }
    }
    navigate("/dashboard");
  };

  useEffect(() => {
    if (user) postAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    if (mode === "forgot") {
      try {
        await api.post("/auth/forgot-password", { email, origin: window.location.origin });
        toast.success("If that email exists, a reset link is on its way.");
        setMode("login");
      } catch {
        toast.error("Could not send reset email — try again.");
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      const res =
        mode === "signup"
          ? await api.post("/auth/register", {
              email,
              password,
              name,
              referral_code: localStorage.getItem("cc_ref") || null,
            })
          : await api.post("/auth/login", { email, password });
      localStorage.setItem("cc_token", res.data.token);
      setUser(res.data.user);
      toast.success(mode === "signup" ? "Welcome to the Cartel." : "Welcome back.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#08080A] text-[#F4F4F5] grain flex flex-col">
      <header className="px-6 h-[72px] flex items-center justify-between max-w-7xl mx-auto w-full">
        <Link to="/" data-testid="nav-logo"><Logo /></Link>
        <Link to="/" className="btn-ghost-gold flex items-center gap-1.5 text-xs font-sub px-4 py-2 rounded-full">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md bg-[#101014] border border-[rgba(212,175,55,0.3)] rounded-2xl p-8 sm:p-10"
          style={{ boxShadow: "0 0 80px rgba(212,175,55,0.08)" }}
        >
          <p className="mono text-[10px] tracking-[0.3em] uppercase text-[#8A7322] text-center mb-2">
            {intent === "checkout" ? "One step from the inside" : "Members Only"}
          </p>
          <h1 className="font-display font-bold text-2xl text-center mb-8">
            {mode === "signup" ? "Claim your seat" : mode === "forgot" ? "Reset access" : "Enter the Cartel"}
          </h1>

          <div className="grid grid-cols-2 gap-1 bg-[#08080A] border border-[#26262F] rounded-full p-1 mb-8">
            {["signup", "login"].map((m) => (
              <button
                key={m}
                data-testid={m === "signup" ? "auth-tab-signup" : "auth-tab-login"}
                onClick={() => setMode(m)}
                className={`mono text-[11px] tracking-[0.15em] uppercase py-2.5 rounded-full transition-all duration-300 ${
                  mode === m ? "bg-gradient-to-r from-[#F5D77F] to-[#D4AF37] text-[#0A0A0C] font-bold" : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {m === "signup" ? "Sign Up" : "Sign In"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div className="relative">
                <User className="w-4 h-4 text-zinc-600 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="auth-name-input"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Display name"
                  className="w-full bg-[#08080A] border border-[#26262F] rounded-full pl-11 pr-4 py-3.5 text-sm outline-none focus:border-[#D4AF37] transition-colors placeholder:text-zinc-600"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="w-4 h-4 text-zinc-600 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="w-full bg-[#08080A] border border-[#26262F] rounded-full pl-11 pr-4 py-3.5 text-sm outline-none focus:border-[#D4AF37] transition-colors placeholder:text-zinc-600"
              />
            </div>
            {mode !== "forgot" && (
              <div className="relative">
                <Lock className="w-4 h-4 text-zinc-600 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="auth-password-input"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password (min 6 chars)"
                  className="w-full bg-[#08080A] border border-[#26262F] rounded-full pl-11 pr-4 py-3.5 text-sm outline-none focus:border-[#D4AF37] transition-colors placeholder:text-zinc-600"
                />
              </div>
            )}
            {mode === "login" && (
              <div className="text-right -mt-1">
                <button
                  type="button"
                  data-testid="auth-forgot-link"
                  onClick={() => setMode("forgot")}
                  className="text-xs text-zinc-500 hover:text-[#E6C665] transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            )}
            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={busy}
              className="btn-gold w-full font-sub text-sm py-4 rounded-full disabled:opacity-60"
            >
              {busy ? "Working..." : mode === "signup" ? "Create Account" : mode === "forgot" ? "Send Reset Link" : "Sign In"}
            </button>
          </form>

          {mode === "forgot" ? (
            <button
              data-testid="auth-back-to-login"
              onClick={() => setMode("login")}
              className="w-full text-center text-xs text-zinc-500 hover:text-[#E6C665] transition-colors mt-6"
            >
              Back to sign in
            </button>
          ) : (
            <>
              <div className="flex items-center gap-4 my-6">
                <div className="h-px flex-1 bg-[#26262F]" />
                <span className="mono text-[10px] tracking-[0.25em] uppercase text-zinc-600">or</span>
                <div className="h-px flex-1 bg-[#26262F]" />
              </div>

              <button
                data-testid="auth-google-btn"
                onClick={googleLogin}
                className="btn-ghost-gold w-full flex items-center justify-center gap-2.5 font-sub font-semibold text-sm py-4 rounded-full"
              >
                <Chrome className="w-4 h-4" /> Continue with Google
              </button>
            </>
          )}

          <p className="text-center text-[11px] text-zinc-600 mt-8 leading-relaxed">
            By joining you agree to play by the Cartel's rules. Membership is $15.99/mo, cancel anytime.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
